import { Link } from "react-router-dom";

interface Expiry {
  propertyId: string;
  propertyName: string;
  unitAlias?: string;
  unitNumber?: string;
  roomAlias?: string;
  roomNumber?: string;
  agreementEndDate: string;
  daysRemaining: number;
}

interface AgreementExpiryBannerProps {
  expiries: Expiry[];
}

export function AgreementExpiryBanner({ expiries }: AgreementExpiryBannerProps) {
  if (expiries.length === 0) return null;

  const sorted = [...expiries].sort((a, b) => a.daysRemaining - b.daysRemaining);
  const nearest = sorted[0];
  const isUrgent = nearest.daysRemaining <= 7;

  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 ${
        isUrgent
          ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/25"
          : "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/25"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`rounded-xl p-2 ${
            isUrgent ? "bg-red-100 dark:bg-red-900/50" : "bg-amber-100 dark:bg-amber-900/50"
          }`}
        >
          <svg
            className={`h-5 w-5 ${isUrgent ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={`font-semibold ${
              isUrgent ? "text-red-800 dark:text-red-200" : "text-amber-800 dark:text-amber-200"
            }`}
          >
            {expiries.length} agreement{expiries.length > 1 ? "s" : ""} expiring
            soon
          </h3>
          <p
            className={`mt-1 text-sm ${
              isUrgent ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300"
            }`}
          >
            <strong>
              {(nearest.unitAlias ?? nearest.roomAlias) || (nearest.unitNumber ?? nearest.roomNumber)
                ? `${nearest.unitAlias ?? nearest.roomAlias ?? nearest.unitNumber ?? nearest.roomNumber} in ${nearest.propertyName}`
                : nearest.propertyName}
            </strong>{" "}
            expires in{" "}
            {nearest.daysRemaining} day{nearest.daysRemaining !== 1 ? "s" : ""}{" "}
            ({new Date(nearest.agreementEndDate).toLocaleDateString("en-IN")})
          </p>
          <Link
            to={`/property/${nearest.propertyId}`}
            className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium ${
              isUrgent ? "text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200" : "text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200"
            }`}
          >
            View property
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}
